/**
 * Bouton « Changer de compte » (en haut à droite des tableaux de bord) pour les
 * admins et les comptes `@jeraisonne.fr` : liste les comptes du groupe et
 * bascule en un toucher (edge fn `admin-switch-account`). Rien pour un compte
 * ordinaire. Depuis le compte de revue store (qui ne peut pas lancer de
 * bascule), seul le retour vers le compte de départ est proposé.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeftRight, Check, X } from 'lucide-react';
import { AvatarCircle, cn } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { ROLE_LABELS } from '@/lib/admin/user-gifts';
import {
  activeAdminReturn,
  canStartAccountSwitch,
  readAdminReturn,
  returnToAdmin,
  switchToInternalAccount,
  useSwitchableAccounts,
} from '@/lib/admin/admin-switch';

export function AccountSwitcher({ className }: { className?: string }) {
  const { session, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const canSwitch = canStartAccountSwitch({
    email: session?.user?.email ?? null,
    role: profile?.role ?? null,
  });
  const saved = activeAdminReturn(readAdminReturn(), session?.user?.id);
  if (!canSwitch && !saved) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Changer de compte"
        title="Changer de compte"
        className={cn(
          'border-ink-200 text-ink-700 hover:bg-ink-50 flex size-11 shrink-0 items-center justify-center rounded-full border bg-white shadow-sm transition-colors active:scale-95',
          className,
        )}
      >
        <ArrowLeftRight className="size-5" aria-hidden />
      </button>
      {open && <AccountSwitcherSheet canSwitch={canSwitch} onClose={() => setOpen(false)} />}
    </>
  );
}

function AccountSwitcherSheet({ canSwitch, onClose }: { canSwitch: boolean; onClose: () => void }) {
  const { session } = useAuth();
  const accounts = useSwitchableAccounts(canSwitch);
  const saved = activeAdminReturn(readAdminReturn(), session?.user?.id);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  async function pick(userId: string) {
    setError(null);
    setBusy(userId);
    try {
      await switchToInternalAccount(userId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La connexion à ce compte a échoué.');
      setBusy(null);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Changer de compte"
        onClick={(e) => e.stopPropagation()}
        className="ocean:bg-[#061433] flex max-h-[85dvh] w-full flex-col rounded-t-3xl bg-white sm:max-w-md sm:rounded-3xl"
      >
        <div className="border-ink-100 flex items-center justify-between gap-2 border-b px-4 py-3">
          <h2 className="font-display text-ink-900 text-lg font-bold">Changer de compte</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink-500 hover:text-ink-900 flex size-11 items-center justify-center rounded-full"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {error && (
            <p role="alert" className="text-error-600 px-2 py-2 text-sm">
              {error}
            </p>
          )}
          {!canSwitch && saved && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setBusy('return');
                void returnToAdmin(saved);
              }}
              className="hover:bg-ink-50 flex min-h-[3.5rem] w-full items-center gap-3 rounded-2xl px-3 text-left active:scale-[0.98]"
            >
              <ArrowLeftRight className="text-primary-600 size-5 shrink-0" aria-hidden />
              <span className="text-ink-900 text-sm font-semibold">
                {busy === 'return' ? 'Retour…' : 'Revenir à mon compte de départ'}
              </span>
            </button>
          )}
          {canSwitch && accounts.isLoading && (
            <p className="text-ink-500 px-2 py-4 text-sm">Chargement des comptes…</p>
          )}
          {canSwitch && accounts.error && (
            <p role="alert" className="text-error-600 px-2 py-2 text-sm">
              {accounts.error.message}
            </p>
          )}
          {canSwitch && (
            <ul className="space-y-1">
              {(accounts.data ?? []).map((a) => {
                const name = a.display_name || a.email || 'Sans nom';
                return (
                  <li key={a.user_id}>
                    <button
                      type="button"
                      disabled={a.is_me || busy !== null}
                      onClick={() => void pick(a.user_id)}
                      className={cn(
                        'flex min-h-[3.5rem] w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition-colors active:scale-[0.98]',
                        a.is_me ? 'bg-primary-50 cursor-default' : 'hover:bg-ink-50',
                      )}
                    >
                      <AvatarCircle name={name} src={a.avatar_url} size="sm" />
                      <span className="min-w-0 flex-1">
                        <span className="text-ink-900 block truncate text-sm font-semibold">
                          {name}
                        </span>
                        <span className="text-ink-500 block truncate text-xs">
                          {ROLE_LABELS[a.role ?? ''] ?? 'Sans rôle'}
                          {a.email ? ` · ${a.email}` : ''}
                        </span>
                      </span>
                      {a.is_me ? (
                        <Check
                          className="text-primary-600 size-5 shrink-0"
                          aria-label="Compte actuel"
                        />
                      ) : busy === a.user_id ? (
                        <span className="text-ink-500 text-xs">Connexion…</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
